/**
 * ASM Custom Theme — Global JavaScript
 * Advanced Skin MedSpa
 * Enhanced with animations, parallax, counters & micro-interactions
 */

/* ==========================================================================
   Scroll Reveal Animations (Enhanced)
   ========================================================================== */

class ScrollReveal {
  constructor() {
    this.elements = document.querySelectorAll('[data-reveal]');
    if (this.elements.length === 0) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = el.dataset.revealDelay || 0;
            setTimeout(() => {
              el.classList.add('is-revealed');
            }, parseInt(delay));
            this.observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    this.elements.forEach((el) => this.observer.observe(el));
  }
}

/* ==========================================================================
   Sticky Header
   ========================================================================== */

class StickyHeader {
  constructor() {
    this.header = document.querySelector('.site-header');
    if (!this.header) return;

    this.scrollThreshold = 100;
    this.lastScroll = 0;
    this.ticking = false;

    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
  }

  onScroll() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  update() {
    const currentScroll = window.scrollY;

    if (currentScroll > this.scrollThreshold) {
      this.header.classList.add('is-sticky');
    } else {
      this.header.classList.remove('is-sticky');
    }

    if (currentScroll > this.lastScroll && currentScroll > 300) {
      this.header.classList.add('is-hidden');
    } else {
      this.header.classList.remove('is-hidden');
    }

    this.lastScroll = currentScroll;
  }
}

/* ==========================================================================
   Mobile Menu
   ========================================================================== */

class MobileMenu {
  constructor() {
    this.toggle = document.querySelector('[data-mobile-menu-toggle]');
    this.drawer = document.querySelector('[data-mobile-menu]');
    this.close = document.querySelector('[data-mobile-menu-close]');
    this.overlay = document.querySelector('[data-mobile-overlay]');
    this.body = document.body;

    if (!this.toggle || !this.drawer) return;

    this.drawer.setAttribute('aria-hidden', 'true');
    this._boundTrapFocus = this._trapFocus.bind(this);

    this.toggle.addEventListener('click', () => this.open());
    if (this.close) this.close.addEventListener('click', () => this.closeMenu());
    if (this.overlay) this.overlay.addEventListener('click', () => this.closeMenu());

    this.drawer.querySelectorAll('[data-submenu-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.toggleSubmenu(e));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.closeMenu();
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.body.classList.add('menu-open');
    if (this.overlay) this.overlay.classList.add('is-visible');
    this.toggle.setAttribute('aria-expanded', 'true');

    document.addEventListener('keydown', this._boundTrapFocus);
    const firstFocusable = this.drawer.querySelector('a[href], button:not([disabled])');
    if (firstFocusable) firstFocusable.focus();
  }

  closeMenu() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.body.classList.remove('menu-open');
    if (this.overlay) this.overlay.classList.remove('is-visible');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this._boundTrapFocus);
    this.toggle.focus();
  }

  isOpen() {
    return this.drawer.classList.contains('is-open');
  }

  _trapFocus(e) {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      this.drawer.querySelectorAll('a[href], button:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"])')
    ).filter(el => el.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  toggleSubmenu(e) {
    const btn = e.currentTarget;
    const submenu = btn.nextElementSibling;
    const isExpanded = btn.getAttribute('aria-expanded') === 'true';

    btn.setAttribute('aria-expanded', !isExpanded);
    if (submenu) {
      submenu.style.maxHeight = isExpanded ? '0' : submenu.scrollHeight + 'px';
    }
  }
}

/* ==========================================================================
   Mega Menu (Desktop)
   ========================================================================== */

class MegaMenu {
  constructor() {
    this.triggers = document.querySelectorAll('[data-mega-menu-trigger]');
    this.panels = document.querySelectorAll('[data-mega-menu-panel]');

    if (this.triggers.length === 0) return;

    this.triggers.forEach((trigger) => {
      const panel = trigger.closest('.nav-item')?.querySelector('[data-mega-menu-panel]');
      if (!panel) return;

      let timeout;
      trigger.addEventListener('mouseenter', () => {
        clearTimeout(timeout);
        this.closeAll();
        this.openPanel(trigger, panel);
      });

      trigger.closest('.nav-item')?.addEventListener('mouseleave', () => {
        timeout = setTimeout(() => this.closePanel(trigger, panel), 200);
      });

      panel.addEventListener('mouseenter', () => clearTimeout(timeout));
      panel.addEventListener('mouseleave', () => {
        timeout = setTimeout(() => this.closePanel(trigger, panel), 200);
      });

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            this.closePanel(trigger, panel);
          } else {
            this.closeAll();
            this.openPanel(trigger, panel);
          }
        }
        if (e.key === 'Escape') {
          this.closePanel(trigger, panel);
          trigger.focus();
        }
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-mega-menu-trigger]') && !e.target.closest('[data-mega-menu-panel]')) {
        this.closeAll();
      }
    });
  }

  openPanel(trigger, panel) {
    trigger.setAttribute('aria-expanded', 'true');
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
  }

  closePanel(trigger, panel) {
    trigger.setAttribute('aria-expanded', 'false');
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
  }

  closeAll() {
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.panels.forEach((p) => {
      p.classList.remove('is-open');
      p.setAttribute('aria-hidden', 'true');
    });
  }
}

/* ==========================================================================
   FAQ Accordion
   ========================================================================== */

class FaqAccordion {
  constructor() {
    document.querySelectorAll('[data-faq-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const item = toggle.closest('[data-faq-item]');
        if (!item) return;
        const content = item.querySelector('[data-faq-content]');
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';

        const group = item.closest('[data-faq-group]');
        if (group) {
          group.querySelectorAll('[data-faq-item]').forEach((other) => {
            if (other !== item) {
              other.querySelector('[data-faq-toggle]')?.setAttribute('aria-expanded', 'false');
              const otherContent = other.querySelector('[data-faq-content]');
              if (otherContent) otherContent.style.maxHeight = '0';
            }
          });
        }

        toggle.setAttribute('aria-expanded', !isOpen);
        if (content) {
          content.style.maxHeight = isOpen ? '0' : content.scrollHeight + 'px';
        }
      });
    });
  }
}

/* ==========================================================================
   Tabs Component
   ========================================================================== */

class TabsComponent {
  constructor() {
    document.querySelectorAll('[data-tabs]').forEach((tabGroup) => {
      const buttons = tabGroup.querySelectorAll('[data-tab-button]');
      const panels = tabGroup.querySelectorAll('[data-tab-panel]');
      const btnArray = Array.from(buttons);

      const activateTab = (btn) => {
        const target = btn.dataset.tabButton;
        buttons.forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
          b.setAttribute('tabindex', '-1');
        });
        panels.forEach((p) => {
          p.classList.remove('is-active');
          p.setAttribute('aria-hidden', 'true');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        btn.setAttribute('tabindex', '0');
        const targetPanel = tabGroup.querySelector(`[data-tab-panel="${target}"]`);
        if (targetPanel) {
          targetPanel.classList.add('is-active');
          targetPanel.setAttribute('aria-hidden', 'false');
        }
      };

      buttons.forEach((btn, i) => {
        btn.addEventListener('click', () => activateTab(btn));
        btn.addEventListener('keydown', (e) => {
          let nextIndex;
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            nextIndex = (i + 1) % btnArray.length;
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            nextIndex = (i - 1 + btnArray.length) % btnArray.length;
          } else {
            return;
          }
          btnArray[nextIndex].focus();
          activateTab(btnArray[nextIndex]);
        });
        if (i > 0 && !btn.classList.contains('is-active')) {
          btn.setAttribute('tabindex', '-1');
        }
      });
    });
  }
}

/* ==========================================================================
   Smooth Scroll for Anchor Links
   ========================================================================== */

class SmoothScroll {
  constructor() {
    document.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (e) => {
        const targetId = link.getAttribute('href');
        if (targetId === '#' || targetId.length < 2) return;

        let target;
        try {
          target = document.querySelector(targetId);
        } catch (_) {
          return;
        }
        if (target) {
          e.preventDefault();
          const headerHeight = document.querySelector('.site-header')?.offsetHeight || 0;
          const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;
          window.scrollTo({ top: targetPosition, behavior: 'smooth' });
        }
      });
    });
  }
}

/* ==========================================================================
   Lazy Load Images (native + fallback)
   ========================================================================== */

class LazyImages {
  constructor() {
    if ('loading' in HTMLImageElement.prototype) return;

    const images = document.querySelectorAll('img[loading="lazy"]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) img.src = img.dataset.src;
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          observer.unobserve(img);
        }
      });
    });

    images.forEach((img) => observer.observe(img));
  }
}

/* ==========================================================================
   NEW: Animated Counter (Trust Bar)
   Numbers count up when scrolled into view
   ========================================================================== */

class AnimatedCounter {
  constructor() {
    this.counters = document.querySelectorAll('[data-counter]');
    if (this.counters.length === 0) return;

    // Also target trust-bar numbers that don't have data-counter
    document.querySelectorAll('.trust-bar__number').forEach((el) => {
      if (!el.dataset.counter) {
        el.dataset.counter = '';
      }
      if (!this.counters.length || !Array.from(this.counters).includes(el)) {
        this.counters = document.querySelectorAll('[data-counter], .trust-bar__number');
      }
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.animateCounter(entry.target);
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    this.counters.forEach((el) => this.observer.observe(el));
  }

  animateCounter(el) {
    const text = el.textContent.trim();
    // Extract number and surrounding text (e.g., "15,000+" or "98%" or "4.9")
    const match = text.match(/^(.*?)([\d,]+\.?\d*)(.*?)$/);
    if (!match) return;

    const prefix = match[1];
    const numStr = match[2].replace(/,/g, '');
    const suffix = match[3];
    const target = parseFloat(numStr);
    const isFloat = numStr.includes('.');
    const hasCommas = match[2].includes(',');
    const duration = 2000;
    const startTime = performance.now();

    el.classList.add('is-counting');

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = target * eased;

      let display;
      if (isFloat) {
        display = current.toFixed(1);
      } else {
        display = Math.round(current).toString();
        if (hasCommas) {
          display = display.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
      }

      el.textContent = prefix + display + suffix;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }
}

/* ==========================================================================
   NEW: Parallax Scrolling
   Subtle parallax effect on elements with data-parallax
   ========================================================================== */

class ParallaxScroll {
  constructor() {
    this.elements = document.querySelectorAll('[data-parallax]');
    if (this.elements.length === 0) return;

    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.ticking = false;
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.update(); // Initial position
  }

  onScroll() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  update() {
    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;

    this.elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const speed = parseFloat(el.dataset.parallax) || 0.15;

      // Only animate when element is near viewport
      if (rect.bottom < -100 || rect.top > viewportHeight + 100) return;

      const offset = (rect.top - viewportHeight / 2) * speed;
      el.style.transform = `translateY(${offset}px)`;
    });
  }
}

/* ==========================================================================
   NEW: Button Ripple Effect
   Click ripple on buttons with .btn-ripple class
   ========================================================================== */

class ButtonRipple {
  constructor() {
    // Apply to all .btn elements for a subtle ripple
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.btn, .asm-btn');
      if (!btn) return;

      const rect = btn.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.classList.add('ripple');

      const size = Math.max(rect.width, rect.height) * 2;
      ripple.style.width = ripple.style.height = size + 'px';
      ripple.style.left = (e.clientX - rect.left - size / 2) + 'px';
      ripple.style.top = (e.clientY - rect.top - size / 2) + 'px';

      // Ensure the button has relative positioning and overflow hidden
      btn.style.position = 'relative';
      btn.style.overflow = 'hidden';
      btn.appendChild(ripple);

      ripple.addEventListener('animationend', () => ripple.remove());
      // Fallback: guarantee the ripple is removed even if the animation
      // never fires (e.g. reduced-motion), so it can never inflate the button.
      setTimeout(() => ripple.remove(), 800);
    });
  }
}

/* ==========================================================================
   NEW: Image Reveal on Load
   Smooth fade-in for images as they load
   ========================================================================== */

class ImageReveal {
  constructor() {
    const images = document.querySelectorAll('.hover-zoom img, .split-content__image, .asm-content-row__image, .team-preview__photo-img');

    images.forEach((img) => {
      if (img.complete) {
        img.classList.add('is-loaded');
      } else {
        img.classList.add('img-reveal');
        img.addEventListener('load', () => {
          requestAnimationFrame(() => {
            img.classList.add('is-loaded');
          });
        });
      }
    });
  }
}

/* ==========================================================================
   NEW: Magnetic Hover Effect
   Buttons/elements slightly follow the cursor on hover
   ========================================================================== */

class MagneticHover {
  constructor() {
    const elements = document.querySelectorAll('.magnetic, .floating-cta');
    if (elements.length === 0) return;

    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    elements.forEach((el) => {
      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        el.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`;
      });

      el.addEventListener('mouseleave', () => {
        el.style.transform = '';
      });
    });
  }
}

/* ==========================================================================
   NEW: Expandable Content (Read More)
   Auto-collapse long content with toggle button
   ========================================================================== */

class ExpandableContent {
  constructor() {
    // Auto-detect long content blocks on service pages
    document.querySelectorAll('.asm-content-row__text, .service-description, [data-expandable]').forEach((el) => {
      if (el.scrollHeight <= 200) return; // Skip short content

      // Wrap if not already wrapped
      if (!el.classList.contains('expandable-content')) {
        el.classList.add('expandable-content', 'is-collapsed');

        const toggle = document.createElement('button');
        toggle.className = 'expand-toggle';
        toggle.innerHTML = 'Read More <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>';
        toggle.setAttribute('aria-expanded', 'false');

        toggle.addEventListener('click', () => {
          const isCollapsed = el.classList.contains('is-collapsed');
          if (isCollapsed) {
            el.style.maxHeight = el.scrollHeight + 'px';
            el.classList.remove('is-collapsed');
            toggle.classList.add('is-expanded');
            toggle.setAttribute('aria-expanded', 'true');
            toggle.innerHTML = 'Read Less <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>';
          } else {
            el.classList.add('is-collapsed');
            el.style.maxHeight = '';
            toggle.classList.remove('is-expanded');
            toggle.setAttribute('aria-expanded', 'false');
            toggle.innerHTML = 'Read More <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>';
          }
        });

        el.parentNode.insertBefore(toggle, el.nextSibling);
      }
    });
  }
}

/* ==========================================================================
   NEW: Smooth Section Transitions
   Adds subtle entrance transitions between sections
   ========================================================================== */

class SectionTransitions {
  constructor() {
    // Observe all main sections for staggered child reveals
    const sections = document.querySelectorAll('.section, [class*="asm-section"]');
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('section-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -30px 0px' }
    );

    sections.forEach((section) => observer.observe(section));
  }
}

/* ==========================================================================
   NEW: Testimonial Auto-Scroll
   Smooth auto-scrolling for testimonial carousels
   ========================================================================== */

class TestimonialCarousel {
  constructor() {
    this.tracks = document.querySelectorAll('.testimonials__track');
    if (this.tracks.length === 0) return;

    this.tracks.forEach((track) => {
      const cards = track.querySelectorAll('.testimonials__card');
      if (cards.length <= 1) return;

      let currentIndex = 0;
      let autoPlayInterval;
      let isHovered = false;

      // Arrow buttons
      const wrapper = track.closest('.testimonials__carousel-wrapper');
      const prevBtn = wrapper?.querySelector('.testimonials__arrow--prev');
      const nextBtn = wrapper?.querySelector('.testimonials__arrow--next');
      const dots = wrapper?.parentElement.querySelectorAll('.testimonials__dot');

      const scrollToIndex = (index) => {
        const card = cards[index];
        if (!card) return;
        track.scrollTo({
          left: card.offsetLeft - track.offsetLeft,
          behavior: 'smooth'
        });
        currentIndex = index;
        updateDots();
      };

      const updateDots = () => {
        if (!dots) return;
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === currentIndex);
        });
      };

      const next = () => {
        currentIndex = (currentIndex + 1) % cards.length;
        scrollToIndex(currentIndex);
      };

      const prev = () => {
        currentIndex = (currentIndex - 1 + cards.length) % cards.length;
        scrollToIndex(currentIndex);
      };

      if (nextBtn) nextBtn.addEventListener('click', () => { next(); resetAutoPlay(); });
      if (prevBtn) prevBtn.addEventListener('click', () => { prev(); resetAutoPlay(); });

      if (dots) {
        dots.forEach((dot, i) => {
          dot.addEventListener('click', () => { scrollToIndex(i); resetAutoPlay(); });
        });
      }

      // Auto-play (every 5 seconds)
      const startAutoPlay = () => {
        autoPlayInterval = setInterval(() => {
          if (!isHovered) next();
        }, 5000);
      };

      const resetAutoPlay = () => {
        clearInterval(autoPlayInterval);
        startAutoPlay();
      };

      track.addEventListener('mouseenter', () => { isHovered = true; });
      track.addEventListener('mouseleave', () => { isHovered = false; });

      startAutoPlay();
      updateDots();
    });
  }
}

/* ==========================================================================
   Initialize All Components
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  // Core components
  new ScrollReveal();
  new StickyHeader();
  new MobileMenu();
  new MegaMenu();
  new FaqAccordion();
  new TabsComponent();
  new SmoothScroll();
  new LazyImages();

  // Enhanced animations & interactions
  new AnimatedCounter();
  new ParallaxScroll();
  new ButtonRipple();
  new ImageReveal();
  new MagneticHover();
  new ExpandableContent();
  new SectionTransitions();
  new TestimonialCarousel();
});
